import React, { useState } from 'react';
import { trpc } from '../../../lib/trpc';
import { cn } from '../../../lib/utils';
import { LoadingSpinner } from '../../atoms';
import { EmailLogsTable } from './';
import { EmailDetailsModal } from './EmailDetailsModal';

export interface EmailDashboardProps {
  className?: string;
  logsLimit?: number;
  showRetryButton?: boolean;
  onEmailLogClick?: (emailLogId: string) => void;
}

export const EmailDashboard: React.FC<EmailDashboardProps> = ({
  className,
  logsLimit = 10,
  showRetryButton = true,
  onEmailLogClick
}) => {
  // State for modal management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEmailLogId, setSelectedEmailLogId] = useState<string>('');

  // Handle email log click - use internal modal if no external handler provided
  const handleEmailLogClick = (emailLogId: string) => {
    if (onEmailLogClick) {
      // Use external handler if provided
      onEmailLogClick(emailLogId);
    } else {
      // Use internal modal
      setSelectedEmailLogId(emailLogId);
      setIsModalOpen(true);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEmailLogId('');
  };
  // Fetch logs data (temporarily disable stats to isolate issue)
  const { isLoading: logsLoading, error: logsError } = trpc.emailLogs.getEmailLogs.useQuery({
    limit: logsLimit,
    offset: 0,
  });

  // Only wait for logs loading (skip stats for now)
  const isLoading = logsLoading;
  const hasError = logsError;

  if (isLoading) {
    return (
      <div className={cn('space-y-6', className)}>
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-secondary-800 mb-2">
            Email Processing Dashboard
          </h2>
          <p className="text-secondary-600">
            Monitor automated email invoice processing and manage daily operations
          </p>
        </div>
        
        {/* Single loading state */}
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">Loading email processing data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={cn('space-y-6', className)}>
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-secondary-800 mb-2">
            Email Processing Dashboard
          </h2>
          <p className="text-secondary-600">
            Monitor automated email invoice processing and manage daily operations
          </p>
        </div>
        
        <div className="text-center py-8">
          <div className="text-red-600 mb-4">
            <p className="text-lg font-medium">Failed to load email data</p>
            <p className="text-sm">Please try refreshing the page</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-secondary-800 mb-2">
          Email Processing Dashboard
        </h2>
        <p className="text-secondary-600">
          Monitor automated email invoice processing and manage daily operations
        </p>
      </div>
      
      {/* Email Statistics Widget - Temporarily disabled */}
      {/* <EmailStatsWidget /> */}
      
      {/* Recent Email Logs */}
      <EmailLogsTable 
        limit={logsLimit}
        showRetryButton={showRetryButton}
        onEmailLogClick={handleEmailLogClick}
      />

      {/* Email Details Modal */}
      <EmailDetailsModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        emailLogId={selectedEmailLogId}
      />
    </div>
  );
};

export default EmailDashboard;
